import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useWarehouses } from '../../hooks/useWarehouses';
import { useProducts } from '../../hooks/useProducts';
import { useSuppliers } from '../../hooks/useSuppliers';
import { useInventory } from '../../hooks/useInventory';
import { CreateInventoryMovementData, InventoryMovementType } from '../../types/warehouse';
import { Product } from '../../types/product';
import { Supplier } from '../../types/supplier';
import LoadingScreen from '../common/LoadingScreen';
import Modal from '../common/Modal';
import toast from 'react-hot-toast';
import { storage } from '../../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  BuildingStorefrontIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  TruckIcon,
  PlusIcon,
  MinusIcon,
  TrashIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ShoppingCartIcon,
  CubeIcon,
  PaperClipIcon,
  DocumentArrowUpIcon
} from '@heroicons/react/24/outline';

interface StockReceiptItem {
  id: string;
  productId: string;
  productName: string;
  productCode: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  unit: string;
}

interface StockReceiptFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  preSelectedWarehouseId?: string;
}

const StockReceiptForm: React.FC<StockReceiptFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  preSelectedWarehouseId
}) => {
  const { t } = useLanguage();
  const { warehouses } = useWarehouses();
  const { products } = useProducts();
  const { suppliers } = useSuppliers();
  const { createInventoryMovement, inventory, fetchInventory } = useInventory();
  
  const [formData, setFormData] = useState<{
    supplierId: string;
    warehouseId: string;
    documentNumber: string;
    receiptDate: string;
    note: string;
  }>({
    supplierId: '',
    warehouseId: preSelectedWarehouseId || '',
    documentNumber: '',
    receiptDate: new Date().toISOString().split('T')[0],
    note: ''
  });

  const [receiptItems, setReceiptItems] = useState<StockReceiptItem[]>([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [loading, setLoading] = useState(false);

  // Beszállító keresés state
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [showSupplierSearch, setShowSupplierSearch] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);

  // Raktár keresés state
  const [warehouseSearchTerm, setWarehouseSearchTerm] = useState('');
  const [showWarehouseSearch, setShowWarehouseSearch] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<any>(null);

  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState<{
    id: string;
    name: string;
    size: number;
    type: string;
    url?: string;
    file?: File;
    uploading?: boolean;
  }[]>([]);
  const [dragActive, setDragActive] = useState(false);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        supplierId: '',
        warehouseId: preSelectedWarehouseId || '',
        documentNumber: '',
        receiptDate: new Date().toISOString().split('T')[0],
        note: ''
      });
      setReceiptItems([]);
      setProductSearchTerm('');
      setShowProductSearch(false);
      setSupplierSearchTerm('');
      setShowSupplierSearch(false);
      setSelectedSupplier(null);
      setWarehouseSearchTerm('');
      setShowWarehouseSearch(false);
      setSelectedWarehouse(preSelectedWarehouseId ? warehouses.find(w => w.id === preSelectedWarehouseId) : null);
      
      // Clear uploaded files
      setUploadedFiles([]);
      setDragActive(false);
      
      // Load inventory data for stock display
      fetchInventory({});
    }
  }, [isOpen, preSelectedWarehouseId, warehouses, fetchInventory]);

  // Click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Close supplier search if clicked outside
      if (showSupplierSearch && !target.closest('.supplier-search-container')) {
        setShowSupplierSearch(false);
      }
      
      // Close warehouse search if clicked outside
      if (showWarehouseSearch && !target.closest('.warehouse-search-container')) {
        setShowWarehouseSearch(false);
      }
      
      // Close product search if clicked outside
      if (showProductSearch && !target.closest('.product-search-container')) {
        setShowProductSearch(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSupplierSearch, showWarehouseSearch, showProductSearch]);

  // Filter products based on search term
  const filteredProducts = products.filter(product =>
    product.isActive && (
      product.name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
      product.code?.toLowerCase().includes(productSearchTerm.toLowerCase())
    )
  );

  // Filter suppliers based on search term
  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.isActive && (
      supplier.name.toLowerCase().includes(supplierSearchTerm.toLowerCase()) ||
      (supplier.companyName && supplier.companyName.toLowerCase().includes(supplierSearchTerm.toLowerCase()))
    )
  );

  // Filter warehouses based on search term
  const filteredWarehouses = warehouses.filter(warehouse =>
    warehouse.isActive && (
      warehouse.name.toLowerCase().includes(warehouseSearchTerm.toLowerCase()) ||
      warehouse.code.toLowerCase().includes(warehouseSearchTerm.toLowerCase())
    )
  );

  // Calculate totals
  const calculateTotals = () => {
    const subtotal = receiptItems.reduce((sum, item) => sum + item.totalPrice, 0);
    return {
      subtotal,
      itemCount: receiptItems.length,
      totalQuantity: receiptItems.reduce((sum, item) => sum + item.quantity, 0)
    };
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addProductToReceipt = (product: Product) => {
    const existingItem = receiptItems.find(item => item.productId === product.id);
    
    if (existingItem) {
      toast.error('Ez a termék már hozzá van adva a bevételezéshez');
      return;
    }

    const newItem: StockReceiptItem = {
      id: Date.now().toString(),
      productId: product.id,
      productName: product.name,
      productCode: product.code || '',
      quantity: 1,
      unitPrice: Number(product.netPrice || 0),
      totalPrice: Number(product.netPrice || 0),
      unit: product.unit || 'db'
    };

    setReceiptItems(prev => [...prev, newItem]);
    setProductSearchTerm('');
    setShowProductSearch(false);
  };

  const selectSupplier = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setFormData(prev => ({ ...prev, supplierId: supplier.id }));
    setSupplierSearchTerm(supplier.name);
    setShowSupplierSearch(false);
  };

  const selectWarehouse = (warehouse: any) => {
    setSelectedWarehouse(warehouse);
    setFormData(prev => ({ ...prev, warehouseId: warehouse.id }));
    setWarehouseSearchTerm(`${warehouse.name} (${warehouse.code})`);
    setShowWarehouseSearch(false);
  };

  const updateReceiptItem = (itemId: string, field: keyof StockReceiptItem, value: number) => {
    setReceiptItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'quantity' || field === 'unitPrice') {
          updatedItem.totalPrice = Number(updatedItem.quantity) * Number(updatedItem.unitPrice);
        }
        return updatedItem;
      }
      return item;
    }));
  };

  const removeReceiptItem = (itemId: string) => {
    setReceiptItems(prev => prev.filter(item => item.id !== itemId));
  };

  // File upload functions
  const handleFileSelect = (files: FileList) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    Array.from(files).forEach(file => {
      if (!validTypes.includes(file.type)) {
        toast.error(`Nem támogatott fájltípus: ${file.name}`);
        return;
      }

      if (file.size > maxSize) {
        toast.error(`A fájl túl nagy: ${file.name} (max 10MB)`);
        return;
      }

      const newFile = {
        id: Date.now().toString() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type,
        file: file,
        uploading: false
      };

      setUploadedFiles(prev => [...prev, newFile]);
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const uploadFilesToStorage = async (): Promise<string[]> => {
    const uploadPromises = uploadedFiles.map(async (fileData) => {
      if (!fileData.file || fileData.url) return fileData.url || '';

      try {
        const timestamp = Date.now();
        const fileName = `receipts/${selectedSupplier?.id || 'unknown'}/${timestamp}_${fileData.file.name}`;
        const fileRef = ref(storage, fileName);
        
        await uploadBytes(fileRef, fileData.file);
        const downloadURL = await getDownloadURL(fileRef);
        
        // Update file data with URL
        setUploadedFiles(prev => prev.map(f => 
          f.id === fileData.id ? { ...f, url: downloadURL } : f
        ));
        
        return downloadURL;
      } catch (error) {
        console.error('Fájl feltöltési hiba:', error);
        toast.error(`Hiba a fájl feltöltésekor: ${fileData.name}`);
        return '';
      }
    });

    const uploadedUrls = await Promise.all(uploadPromises);
    return uploadedUrls.filter(url => url !== '');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedSupplier || !selectedWarehouse) {
      toast.error('Kérlek válassz beszállítót és raktárt!');
      return;
    }

    if (receiptItems.length === 0) {
      toast.error('Adj hozzá legalább egy terméket a bevételezéshez!');
      return;
    }

    setLoading(true);
    try {
      // Upload files first if any
      let uploadedFileUrls: string[] = [];
      if (uploadedFiles.length > 0) {
        toast.loading('Dokumentumok feltöltése...', { id: 'file-upload' });
        uploadedFileUrls = await uploadFilesToStorage();
        toast.dismiss('file-upload');
        
        if (uploadedFileUrls.length > 0) {
          toast.success(`${uploadedFileUrls.length} dokumentum sikeresen feltöltve`);
        }
      }

      // Create inventory movements for each item
      for (const item of receiptItems) {
        const noteWithFiles = [
          `Bevételezés beszállítótól: ${selectedSupplier.name}`,
          formData.note ? formData.note : '',
          `Egységár: ${item.unitPrice} Ft`,
          uploadedFileUrls.length > 0 ? `Dokumentumok: ${uploadedFileUrls.length} db` : ''
        ].filter(Boolean).join(' - ');

        const movementData: CreateInventoryMovementData = {
          productId: item.productId,
          warehouseId: selectedWarehouse.id,
          quantity: item.quantity,
          type: InventoryMovementType.STOCK_IN,
          documentNumber: formData.documentNumber || undefined,
          documentType: 'RECEIPT',
          note: noteWithFiles,
          attachments: uploadedFileUrls.length > 0 ? uploadedFileUrls : undefined
        };

        await createInventoryMovement(movementData);
      }

      toast.success(`Bevételezés sikeresen rögzítve! ${receiptItems.length} tétel befogadva.`);
      
      // Lista frissítése - kis késés a Firebase persistálás miatt
      setTimeout(() => {
        console.log('Attempting to refresh inventory movements list after receipt');
        if ((window as any).refreshInventoryMovements) {
          (window as any).refreshInventoryMovements();
        } else {
          console.warn('refreshInventoryMovements function not found on window');
        }
      }, 500);
      
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Hiba a bevételezés rögzítésekor:', error);
      toast.error('Hiba történt a bevételezés rögzítésekor');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const { subtotal, itemCount, totalQuantity } = calculateTotals();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Készlet bevételezés"
      size="6xl"
    >
      <form onSubmit={handleSubmit} className="p-4 space-y-6">
          {/* Beszállító és raktár kiválasztás */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <TruckIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="p-4 flex-1">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Beszállító és raktár adatai</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Beszállító */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Beszállító *
                    </label>
                    {selectedSupplier ? (
                      <div className="flex items-center justify-between p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-gray-100">{selectedSupplier.name}</div>
                          {selectedSupplier.companyName && selectedSupplier.companyName !== selectedSupplier.name && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">{selectedSupplier.companyName}</div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedSupplier(null);
                            setFormData(prev => ({ ...prev, supplierId: '' }));
                            setSupplierSearchTerm('');
                            setShowSupplierSearch(true);
                          }}
                          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 text-sm font-medium"
                        >
                          Módosítás
                        </button>
                      </div>
                    ) : (
                      <div className="relative supplier-search-container">
                        <MagnifyingGlassIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                        <input
                          type="text"
                          value={supplierSearchTerm}
                          onChange={(e) => {
                            setSupplierSearchTerm(e.target.value);
                            setShowSupplierSearch(true);
                          }}
                          onFocus={() => setShowSupplierSearch(true)}
                          placeholder="Beszállító keresése..."
                          className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 
                                   placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400"
                        />
                        
                        {showSupplierSearch && (
                          <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {supplierSearchTerm.length > 0 ? (
                              filteredSuppliers.length > 0 ? (
                                filteredSuppliers.slice(0, 10).map((supplier) => (
                                  <button
                                    key={supplier.id}
                                    type="button"
                                    onClick={() => selectSupplier(supplier)}
                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0"
                                  >
                                    <div className="font-medium text-gray-900 dark:text-gray-100">{supplier.name}</div>
                                    {supplier.companyName && supplier.companyName !== supplier.name && (
                                      <div className="text-sm text-gray-500 dark:text-gray-400">{supplier.companyName}</div>
                                    )}
                                  </button>
                                ))
                              ) : (
                                <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                                  Nincs találat
                                </div>
                              )
                            ) : (
                              suppliers.filter(s => s.isActive).slice(0, 10).map((supplier) => (
                                <button
                                  key={supplier.id}
                                  type="button"
                                  onClick={() => selectSupplier(supplier)}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0"
                                >
                                  <div className="font-medium text-gray-900 dark:text-gray-100">{supplier.name}</div>
                                  {supplier.companyName && supplier.companyName !== supplier.name && (
                                    <div className="text-sm text-gray-500 dark:text-gray-400">{supplier.companyName}</div>
                                  )}
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Raktár */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Raktár *
                    </label>
                    {selectedWarehouse ? (
                      <div className="flex items-center justify-between p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-gray-100">{selectedWarehouse.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">Kód: {selectedWarehouse.code}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedWarehouse(null);
                            setFormData(prev => ({ ...prev, warehouseId: '' }));
                            setWarehouseSearchTerm('');
                            setShowWarehouseSearch(true);
                          }}
                          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 text-sm font-medium"
                        >
                          Módosítás
                        </button>
                      </div>
                    ) : (
                      <div className="relative warehouse-search-container">
                        <MagnifyingGlassIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                        <input
                          type="text"
                          value={warehouseSearchTerm}
                          onChange={(e) => {
                            setWarehouseSearchTerm(e.target.value);
                            setShowWarehouseSearch(true);
                          }}
                          onFocus={() => setShowWarehouseSearch(true)}
                          placeholder="Raktár keresése..."
                          className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 
                                   placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400"
                        />
                        
                        {showWarehouseSearch && (
                          <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {warehouseSearchTerm.length > 0 ? (
                              filteredWarehouses.length > 0 ? (
                                filteredWarehouses.slice(0, 10).map((warehouse) => (
                                  <button
                                    key={warehouse.id}
                                    type="button"
                                    onClick={() => selectWarehouse(warehouse)}
                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0"
                                  >
                                    <div className="font-medium text-gray-900 dark:text-gray-100">{warehouse.name}</div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">Kód: {warehouse.code}</div>
                                  </button>
                                ))
                              ) : (
                                <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                                  Nincs találat
                                </div>
                              )
                            ) : (
                              warehouses.filter(w => w.isActive).slice(0, 10).map((warehouse) => (
                                <button
                                  key={warehouse.id}
                                  type="button"
                                  onClick={() => selectWarehouse(warehouse)}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0"
                                >
                                  <div className="font-medium text-gray-900 dark:text-gray-100">{warehouse.name}</div>
                                  <div className="text-sm text-gray-500 dark:text-gray-400">Kód: {warehouse.code}</div>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bizonylat adatok */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-green-100 to-teal-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
                <DocumentTextIcon className="w-5 h-5 text-green-600" />
              </div>
              <div className="p-4 flex-1">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Bizonylat adatok</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Bizonylat szám */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Bizonylat szám
                    </label>
                    <input
                      type="text"
                      value={formData.documentNumber}
                      onChange={(e) => handleInputChange('documentNumber', e.target.value)}
                      placeholder="pl. BEV-2024-001"
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                               placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>

                  {/* Bevételezés dátuma */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Bevételezés dátuma *
                    </label>
                    <input
                      type="date"
                      value={formData.receiptDate}
                      onChange={(e) => handleInputChange('receiptDate', e.target.value)}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                               placeholder-gray-500 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Termék hozzáadás */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-purple-100 to-fuchsia-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
                <CubeIcon className="w-5 h-5 text-purple-600" />
              </div>
              <div className="p-4 flex-1">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Termék hozzáadása</h3>
                
                <div className="relative product-search-container">
                  <MagnifyingGlassIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={productSearchTerm}
                    onChange={(e) => {
                      setProductSearchTerm(e.target.value);
                      setShowProductSearch(true);
                    }}
                    onFocus={() => setShowProductSearch(true)}
                    placeholder="Termék keresése név vagy kód alapján..."
                                            className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                  
                  {showProductSearch && (
                                            <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {productSearchTerm.length > 0 
                        ? filteredProducts.length > 0 
                          ? filteredProducts.slice(0, 10).map((product) => (
                              <button
                                key={product.id}
                                type="button"
                                onClick={() => addProductToReceipt(product)}
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                              >
                                <div className="font-medium text-gray-900 dark:text-gray-100">{product.name}</div>
                                <div className="text-sm text-gray-500 dark:text-gray-400 flex justify-between">
                                  <span>Kód: {product.code}</span>
                                  <span>{product.netPrice} Ft</span>
                                </div>
                                <div className="text-xs mt-1">
                                  <span className={(() => {
                                    const stockByWarehouse = inventory
                                      .filter(inv => inv.productId === product.id)
                                      .map(inv => inv.quantity - (inv.reservedQuantity || 0))
                                      .reduce((sum, v) => sum + v, 0);
                                    return stockByWarehouse > 0
                                      ? 'text-green-600 dark:text-green-400 font-medium'
                                      : 'text-red-600 dark:text-red-400 font-medium';
                                  })()}>
                                    Jelenlegi készlet: {(() => {
                                      const stockByWarehouse = inventory
                                        .filter(inv => inv.productId === product.id)
                                        .map(inv => inv.quantity - (inv.reservedQuantity || 0))
                                        .reduce((sum, v) => sum + v, 0);
                                      return `${stockByWarehouse} ${product.unit || 'db'}`;
                                    })()}
                                  </span>
                                </div>
                              </button>
                            ))
                          : (
                              <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                                Nincs találat
                              </div>
                            )
                        : products.filter(p => p.isActive).slice(0, 10).map((product) => (
                            <button
                              key={product.id}
                              type="button"
                              onClick={() => addProductToReceipt(product)}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                            >
                              <div className="font-medium text-gray-900 dark:text-gray-100">{product.name}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400 flex justify-between">
                                <span>Kód: {product.code}</span>
                                <span>{product.netPrice} Ft</span>
                              </div>
                              <div className="text-xs mt-1">
                                <span className={(() => {
                                  const stockByWarehouse = inventory
                                    .filter(inv => inv.productId === product.id)
                                    .map(inv => inv.quantity - (inv.reservedQuantity || 0))
                                    .reduce((sum, v) => sum + v, 0);
                                  return stockByWarehouse > 0
                                    ? 'text-green-600 dark:text-green-400 font-medium'
                                    : 'text-red-600 dark:text-red-400 font-medium';
                                })()}>
                                  Jelenlegi készlet: {(() => {
                                    const stockByWarehouse = inventory
                                      .filter(inv => inv.productId === product.id)
                                      .map(inv => inv.quantity - (inv.reservedQuantity || 0))
                                      .reduce((sum, v) => sum + v, 0);
                                    return `${stockByWarehouse} ${product.unit || 'db'}`;
                                  })()}
                                </span>
                              </div>
                            </button>
                          ))
                      }
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bevételezett tételek listája */}
          {receiptItems.length > 0 && (
            <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex">
                <div className="p-4 bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
                  <ShoppingCartIcon className="w-5 h-5 text-orange-600" />
                </div>
                <div className="p-4 flex-1">
                  <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">
                    Bevételezett tételek ({itemCount} tétel)
                  </h3>
                  
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    {/* Desktop táblázat */}
                    <div className="hidden lg:block overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-100 dark:bg-gray-600">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Termék
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Mennyiség
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Egységár
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Összesen
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Műveletek
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {receiptItems.map((item) => (
                            <tr key={item.id}>
                              <td className="px-4 py-3">
                                <div>
                                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.productName}</div>
                                  <div className="text-sm text-gray-500 dark:text-gray-400">{item.productCode}</div>
                                  <div className="text-xs mt-1">
                                    <span className={(() => {
                                      const stockByWarehouse = inventory
                                        .filter(inv => inv.productId === item.productId)
                                        .map(inv => inv.quantity - (inv.reservedQuantity || 0))
                                        .reduce((sum, v) => sum + v, 0);
                                      return stockByWarehouse > 0
                                        ? 'text-green-600 dark:text-green-400 font-medium'
                                        : 'text-red-600 dark:text-red-400 font-medium';
                                    })()}>
                                      Jelenlegi készlet: {(() => {
                                        const stockByWarehouse = inventory
                                          .filter(inv => inv.productId === item.productId)
                                          .map(inv => inv.quantity - (inv.reservedQuantity || 0))
                                          .reduce((sum, v) => sum + v, 0);
                                        return `${stockByWarehouse} ${item.unit || 'db'}`;
                                      })()}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <div className="flex items-center justify-center space-x-2">
                                  <button
                                    type="button"
                                    onClick={() => updateReceiptItem(item.id, 'quantity', Math.max(1, item.quantity - 1))}
                                    className="p-1 text-gray-400 hover:text-gray-600"
                                  >
                                    <MinusIcon className="h-4 w-4" />
                                  </button>
                                  <input
                                    type="number"
                                    value={item.quantity}
                                    onChange={(e) => updateReceiptItem(item.id, 'quantity', Math.max(1, parseFloat(e.target.value) || 1))}
                                    className="w-16 text-center border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                                    min="1"
                                    step="0.01"
                                  />
                                  <span className="text-xs text-gray-500 dark:text-gray-400">{item.unit}</span>
                                  <button
                                    type="button"
                                    onClick={() => updateReceiptItem(item.id, 'quantity', item.quantity + 1)}
                                    className="p-1 text-gray-400 hover:text-gray-600"
                                  >
                                    <PlusIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <input
                                  type="number"
                                  value={item.unitPrice}
                                  onChange={(e) => updateReceiptItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                                  className="w-20 text-right border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                                  min="0"
                                  step="0.01"
                                />
                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">Ft</span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {Number(item.totalPrice).toLocaleString('hu-HU')} Ft
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  type="button"
                                  onClick={() => removeReceiptItem(item.id)}
                                  className="text-red-600 hover:text-red-800 p-1"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobil kártyák */}
                    <div className="lg:hidden space-y-3 p-4">
                      {receiptItems.map((item) => (
                        <div key={item.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-gray-100">{item.productName}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{item.productCode}</div>
                              <div className="text-xs mt-1">
                                <span className={(() => {
                                  const stockByWarehouse = inventory
                                    .filter(inv => inv.productId === item.productId)
                                    .map(inv => inv.quantity - (inv.reservedQuantity || 0))
                                    .reduce((sum, v) => sum + v, 0);
                                  return stockByWarehouse > 0
                                    ? 'text-green-600 dark:text-green-400 font-medium'
                                    : 'text-red-600 dark:text-red-400 font-medium';
                                })()}>
                                  Jelenlegi készlet: {(() => {
                                    const stockByWarehouse = inventory
                                      .filter(inv => inv.productId === item.productId)
                                      .map(inv => inv.quantity - (inv.reservedQuantity || 0))
                                      .reduce((sum, v) => sum + v, 0);
                                    return `${stockByWarehouse} ${item.unit || 'db'}`;
                                  })()}
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeReceiptItem(item.id)}
                              className="text-red-600 hover:text-red-800 p-1"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Mennyiség</label>
                              <div className="flex items-center space-x-2">
                                <button
                                  type="button"
                                  onClick={() => updateReceiptItem(item.id, 'quantity', Math.max(1, item.quantity - 1))}
                                  className="p-1 text-gray-400 hover:text-gray-600"
                                >
                                  <MinusIcon className="h-4 w-4" />
                                </button>
                                <input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => updateReceiptItem(item.id, 'quantity', Math.max(1, parseFloat(e.target.value) || 1))}
                                  className="w-16 text-center border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                                  min="1"
                                  step="0.01"
                                />
                                <button
                                  type="button"
                                  onClick={() => updateReceiptItem(item.id, 'quantity', item.quantity + 1)}
                                  className="p-1 text-gray-400 hover:text-gray-600"
                                >
                                  <PlusIcon className="h-4 w-4" />
                                </button>
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400">{item.unit}</span>
                            </div>
                            
                            <div>
                              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Egységár</label>
                                                              <input
                                  type="number"
                                  value={item.unitPrice}
                                  onChange={(e) => updateReceiptItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                                  className="w-full text-right border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                                  min="0"
                                  step="0.01"
                                />
                            </div>
                          </div>
                          
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600 dark:text-gray-400">Összesen:</span>
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {Number(item.totalPrice).toLocaleString('hu-HU')} Ft
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Összesítő */}
                    <div className="bg-gray-100 dark:bg-gray-600 px-4 py-3 border-t border-gray-200 dark:border-gray-600">
                      <div className="flex justify-between items-center text-sm">
                        <div className="flex space-x-6">
                          <span className="text-gray-700 dark:text-gray-300">Tételek száma: <strong>{itemCount}</strong></span>
                          <span className="text-gray-700 dark:text-gray-300">Összes mennyiség: <strong>{totalQuantity}</strong></span>
                        </div>
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          Összesen: {Number(subtotal).toLocaleString('hu-HU')} Ft
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Megjegyzés */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-yellow-100 to-amber-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
                <ClipboardDocumentListIcon className="w-5 h-5 text-yellow-600" />
              </div>
              <div className="p-4 flex-1">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Megjegyzés</h3>
                
                <textarea
                  value={formData.note}
                  onChange={(e) => handleInputChange('note', e.target.value)}
                  rows={3}
                  placeholder="Opcionális megjegyzés a bevételezéshez..."
                  className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                           placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Dokumentum feltöltés */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
                <DocumentArrowUpIcon className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="p-4 flex-1">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Dokumentumok feltöltése</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-4">
                  Szállítólevél, számla vagy egyéb dokumentumok (PDF, Word, kép formátumban, max 10MB)
                </p>
                
                {/* Drag & Drop terület */}
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    dragActive
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <PaperClipIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    Húzza ide a fájlokat vagy{' '}
                    <label className="cursor-pointer text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium">
                      böngészés
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                        onChange={(e) => e.target.files && handleFileSelect(e.target.files)}
                        className="hidden"
                      />
                    </label>
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Támogatott formátumok: PDF, Word, JPG, PNG, GIF (max 10MB)
                  </p>
                </div>

                {/* Feltöltött fájlok listája */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                      Feltöltött dokumentumok ({uploadedFiles.length})
                    </h4>
                    <div className="space-y-2">
                      {uploadedFiles.map((file) => (
                        <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              {file.type.startsWith('image/') ? (
                                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                              ) : file.type === 'application/pdf' ? (
                                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                                  <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                  </svg>
                                </div>
                              ) : (
                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                  <DocumentTextIcon className="w-4 h-4 text-blue-600" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {formatFileSize(file.size)}
                                {file.uploading && ' - Feltöltés...'}
                                {file.url && ' - Feltöltve ✓'}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(file.id)}
                            className="flex-shrink-0 text-red-600 hover:text-red-800 p-1"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Gombok */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Mégse
            </button>
            <button
              type="submit"
              disabled={loading || !selectedSupplier || !selectedWarehouse || receiptItems.length === 0}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 mr-2">
                    <LoadingScreen />
                  </div>
                  <span>Rögzítés...</span>
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Bevételezés rögzítése ({itemCount} tétel)
                </>
              )}
            </button>
          </div>
        </form>
    </Modal>
  );
};

export default StockReceiptForm; 